// ============================================================================
// JARVISTRAVEL - SAMPLE DATA
// All data for trips, activities, properties, etc.
// ============================================================================

import type { DayShift, ExploreActivity, Property, Notification, JournalTrip } from '@/app/types';
import { toLocalYmd } from '@/app/utils/dateOnly';

// ============================================================================
// SAMPLE TRIPS DATA
// ============================================================================

// ============================================================================
// DEMO TRIP DATES — derived, not pinned
// ============================================================================
//
// `trip-rome` is the demo's LIVE trip: the whole demo exists to show what an
// in-progress trip looks like. Its dates used to be literals (2026-07-28 →
// 08-03), which meant the demo was only correct for the seven days the calendar
// happened to agree. Before that window every visitor saw a trip that had not
// started; after it, one that had already finished — permanently, and silently,
// because a stale date is not a failure (JAR-609).
//
// Deriving them from today removes the coupling instead of postponing it.
// Bumping the literals would just re-arm the same trap a few weeks out.
//
// The authored data is written against day 1..7 and the offsets below preserve
// the spacing exactly, so itinerary days, receipts, expenses and reservations
// all shift together — receipt-to-itinerary matching keeps demonstrating.

/** The demo trip started this many days ago. Day 3 of 7 — far enough in to have
 *  a journal worth showing, with days still ahead. */
const DEMO_TRIP_STARTED_DAYS_AGO = 2;

const startOfDemoTrip = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - DEMO_TRIP_STARTED_DAYS_AGO);
  return d;
};

/** ISO date (YYYY-MM-DD) for day N of the demo trip, 1-indexed. */
const demoDay = (dayNumber: number): string => {
  const d = startOfDemoTrip();
  d.setDate(d.getDate() + (dayNumber - 1));
  return toLocalYmd(d);
};

/** The demo flies in from New York (UTC-4, flightEngine's demo home) to Rome
 *  (UTC+2): a 6-hour shift the traveler recovers from a day at a time, which the
 *  Fatigue Index reads as jet lag (JAR-1678). */
const demoShift = (dayNumber: number): DayShift => ({ hours: 6, daysSince: dayNumber - 1 });

/**
 * Local, unzoned timestamp on day N of the demo trip — e.g. demoDayAt(1, '19:45:00').
 *
 * No `Z`. This feeds ReceiptEvidence.date_time, which is consumed as a calendar
 * date (BudgetManager slices it to ten characters), so a UTC-zoned value shows
 * a demo receipt on the previous day for anyone east of Greenwich — the JAR-610
 * L3 bug still live in demo mode. It also has to match the shape real receipts
 * carry, or the two forms coexist in one list and TripJournalStory's
 * localeCompare sort orders them against each other by their suffix.
 */
const demoDayAt = (dayNumber: number, time: string): string => `${demoDay(dayNumber)}T${time}`;

/**
 * The instant version, for `created_at` only.
 *
 * created_at records when a row was written, which genuinely is a moment in
 * time — real receipts set it from toISOString(). Keeping demo data on the same
 * side of that line is the point of having two helpers rather than one.
 */
const demoDayAtUtc = (dayNumber: number, time: string): string => `${demoDay(dayNumber)}T${time}Z`;

/** Day 1 and the last day, exported so tests can pin a clock RELATIVE to the
 *  data rather than to a literal that would drift away from it. */
export const DEMO_TRIP_START = demoDay(1);
export const DEMO_TRIP_END = demoDay(7);

/** A calendar date N days from today (N<0 past, N>0 future), for the sample trips
 *  that are not the live one — so the Planning and Completed lanes stay populated
 *  whatever day the demo opens (JAR-1997). */
const ymdFromToday = (daysOffset: number): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + daysOffset);
  return toLocalYmd(d);
};

const TRIPS: JournalTrip[] = [
  {
    id: 'trip-rome',
    name: 'Roman Holiday',
    destination: 'Rome, Italy',
    country: 'Italy',
    dates: { checkin: demoDay(1), checkout: demoDay(7) },
    status: 'Draft',
    travelers: { adults: 2, children: 0 },
    travelerNames: ['Alex Thompson', 'Sarah Thompson'],
    budget: {
      total: 5500, spent: 1326.5, remaining: 4173.5, currency: 'USD', perPerson: 2750,
      cardBudget: 3500, cashBudget: 2000,
      categories: [
        { id: 'flights', name: 'Flights', allocated: 1800, spent: 1200, icon: '✈️', color: '#0EA5E9' },
        { id: 'hotels', name: 'Hotels', allocated: 1400, spent: 0, icon: '🏨', color: '#6E4A62' },
        { id: 'food', name: 'Food', allocated: 1000, spent: 126.5, icon: '🍽️', color: '#059669' },
        { id: 'activities', name: 'Activities', allocated: 800, spent: 0, icon: '🎭', color: '#C98A2E' },
        { id: 'transport', name: 'Transport', allocated: 300, spent: 0, icon: '🚕', color: '#2E6FA3' },
        { id: 'shopping', name: 'Shopping', allocated: 200, spent: 0, icon: '🛍️', color: '#B23F2C' },
      ],
      transactions: [
        { id: 't1', description: 'Round-trip flights', amount: 1200, category: 'flights', date: demoDay(1), method: 'card' },
        { id: 't2', description: 'Coffee at airport', amount: 12, category: 'food', date: demoDay(1), method: 'cash' },
        { id: 't3', description: 'Gelato', amount: 8, category: 'food', date: demoDay(2), method: 'cash' },
        { id: 't4', description: 'Lunch at cafe', amount: 14, category: 'food', date: demoDay(2), method: 'cash' },
        { id: 't5', description: 'Dinner at Roscioli', amount: 92.5, category: 'food', date: demoDay(1), method: 'card' },
      ],
    },
    fatigueIndex: {
      score: 6, level: 'Balanced',
      breakdown: {
        travelTime: { score: 40, weight: 0.25, description: 'Time spent in transit' },
        activityDensity: { score: 55, weight: 0.30, description: 'Number of activities per day' },
        restPeriods: { score: 60, weight: 0.20, description: 'Scheduled downtime' },
        jetLag: { score: 35, weight: 0.15, description: 'Timezone adjustment' },
        walkingDistance: { score: 45, weight: 0.10, description: 'Daily walking distance' },
      },
      recommendations: [
        'Your itinerary looks well-balanced!',
        'Consider adding a rest day mid-trip',
        'Plan afternoon activities to allow morning recovery',
      ],
      lastUpdated: new Date().toISOString(),
    },
    stats: { restaurants: 5, attractions: 8, flights: 2, photos: 0, trains: 1, milesTraveled: 4892 },
    // Receipt amounts are trip-budget-currency numbers end to end (matching,
    // transactions, journal spend) — one currency per trip until real FX
    // conversion arrives with the backend evidence fusion (JAR-288/JAR-140).
    receipts: [
      {
        id: 'rcpt-rome-1', trip_id: 'trip-rome', merchant: 'Roscioli', amount: 92.5, currency: 'USD', date_time: demoDayAt(1, '19:45:00'), location_hint: 'Via dei Giubbonari, Rome', confidence: 0.96, match_type: 'auto', matched_item_id: 'a5', user_confirmed: true, created_at: demoDayAtUtc(1, '20:30:00'),
        items: [
          { name: 'Cacio e pepe', price: 22 },
          { name: 'Carbonara', price: 24 },
          { name: 'Carciofi alla giudia', price: 16 },
          { name: 'House wine (½L)', price: 22.5 },
          { name: 'Coperto ×2', price: 8 },
        ],
      },
      {
        id: 'rcpt-rome-2', trip_id: 'trip-rome', merchant: 'Colosseo Ticket Office', amount: 55, currency: 'USD', date_time: demoDayAt(3, '09:20:00'), location_hint: 'Piazza del Colosseo', confidence: 0.91, match_type: 'auto', matched_item_id: 'c1', user_confirmed: true, created_at: demoDayAtUtc(3, '09:25:00'),
        items: [
          { name: 'Adult entry ×2', price: 36 },
          { name: 'Arena floor access', price: 19 },
        ],
      },
      // Unmatched cash receipt — stays itemless on purpose: the R4b pivot-place demo.
      { id: 'rcpt-rome-3', trip_id: 'trip-rome', merchant: 'Gelateria del Teatro', amount: 9.5, currency: 'USD', date_time: demoDayAt(2, '16:10:00'), location_hint: 'Via dei Coronari', confidence: 0.42, match_type: 'cash', user_confirmed: false, created_at: demoDayAtUtc(2, '16:12:00') },
    ],
    itinerary: [
      // fiScore / fiLevel are the engine's own (computeDayScore), and
      // sampleData.test.ts holds every day to it: a stale hand-written score
      // flips the moment any edit re-scores the trip (JAR-1425).
      {
        id: 'day-1', date: demoDay(1), dayNumber: 1, location: 'Rome', tzShift: demoShift(1),
        fiScore: 6, fiLevel: 'Balanced',
        activities: [
          { id: 'a1', name: 'Arrival at FCO', type: 'flight', time: '09:30', duration: 30, location: { name: 'Fiumicino Airport', address: 'Via dell\'Aeroporto di Fiumicino', lat: 41.8003, lng: 12.2389 }, booked: true, fatigueImpact: 'balanced', cost: 0, notes: 'Terminal 3' },
          { id: 'a2', name: 'Transfer to Hotel', type: 'transport', time: '10:30', duration: 45, location: { name: 'Rome City Center', lat: 41.9028, lng: 12.4964 }, booked: true, fatigueImpact: 'chill', cost: 48 },
          { id: 'a3', name: 'Check in at Hotel de Russie', type: 'accommodation', time: '14:00', duration: 30, location: { name: 'Hotel de Russie', address: 'Via del Babuino, 9', lat: 41.9106, lng: 12.4768 }, booked: true, fatigueImpact: 'chill', cost: 0 },
          { id: 'a4', name: 'Walk around Piazza del Popolo', type: 'attraction', time: '15:30', duration: 120, location: { name: 'Piazza del Popolo', lat: 41.9109, lng: 12.4763 }, booked: false, fatigueImpact: 'balanced', cost: 0 },
          { id: 'a5', name: 'Welcome dinner at Roscioli', type: 'restaurant', time: '19:30', duration: 120, location: { name: 'Roscioli', address: 'Via dei Giubbonari, 21', phone: '+39 06 6875287', lat: 41.8939, lng: 12.4750 }, booked: true, fatigueImpact: 'chill', cost: 85, notes: 'Reservation confirmed' },
        ],
      },
      {
        id: 'day-2', date: demoDay(2), dayNumber: 2, location: 'Vatican City', tzShift: demoShift(2),
        fiScore: 7, fiLevel: 'Packed',
        activities: [
          { id: 'b1', name: 'Vatican Museums Tour', type: 'attraction', time: '09:00', duration: 180, location: { name: 'Vatican Museums', address: 'Viale Vaticano', lat: 41.9065, lng: 12.4536 }, booked: true, fatigueImpact: 'packed', cost: 45, notes: 'Skip-the-line tickets', providerUrl: 'https://www.museivaticani.va/' },
          { id: 'b2', name: 'Sistine Chapel', type: 'attraction', time: '12:00', duration: 60, location: { name: 'Sistine Chapel', lat: 41.9029, lng: 12.4545 }, booked: true, fatigueImpact: 'balanced', cost: 0, notes: 'Included in Vatican tour' },
          { id: 'b3', name: 'Lunch at Pizzarium Bonci', type: 'restaurant', time: '13:30', duration: 90, location: { name: 'Pizzarium Bonci', address: 'Via della Meloria, 43', lat: 41.9070, lng: 12.4467 }, booked: false, fatigueImpact: 'chill', cost: 25 },
          { id: 'b4', name: "St. Peter's Basilica", type: 'attraction', time: '15:30', duration: 90, location: { name: "St. Peter's Basilica", lat: 41.9022, lng: 12.4539 }, booked: false, fatigueImpact: 'balanced', cost: 0 },
          { id: 'b5', name: 'Rest at Hotel', type: 'rest', time: '17:30', duration: 120, location: { name: 'Hotel de Russie', lat: 41.9106, lng: 12.4768 }, booked: false, fatigueImpact: 'chill', cost: 0 },
          { id: 'b6', name: 'Dinner at Da Enzo al 29', type: 'restaurant', time: '20:00', duration: 120, location: { name: 'Da Enzo al 29', address: 'Via dei Vascellari, 29', phone: '+39 06 5812260', lat: 41.8890, lng: 12.4775 }, booked: true, fatigueImpact: 'chill', cost: 65 },
        ],
      },
      {
        id: 'day-3', date: demoDay(3), dayNumber: 3, location: 'Ancient Rome', tzShift: demoShift(3),
        fiScore: 7, fiLevel: 'Packed',
        activities: [
          { id: 'c1', name: 'Colosseum Tour', type: 'attraction', time: '09:30', duration: 150, location: { name: 'Colosseum', address: 'Piazza del Colosseo, 1', lat: 41.8902, lng: 12.4922 }, booked: true, fatigueImpact: 'packed', cost: 55, notes: 'Underground access included', providerUrl: 'https://www.coopculture.it/' },
          { id: 'c2', name: 'Roman Forum', type: 'attraction', time: '12:30', duration: 120, location: { name: 'Roman Forum', lat: 41.8925, lng: 12.4853 }, booked: true, fatigueImpact: 'balanced', cost: 0, notes: 'Included with Colosseum ticket' },
          { id: 'c3', name: 'Lunch at Luzzi', type: 'restaurant', time: '15:00', duration: 90, location: { name: 'Luzzi', address: 'Via di San Giovanni in Laterano, 88', lat: 41.8869, lng: 12.4975 }, booked: false, fatigueImpact: 'chill', cost: 30 },
          { id: 'c4', name: 'Palatine Hill', type: 'attraction', time: '16:30', duration: 90, location: { name: 'Palatine Hill', lat: 41.8892, lng: 12.4870 }, booked: true, fatigueImpact: 'balanced', cost: 0 },
        ],
      },
      {
        id: 'day-4', date: demoDay(4), dayNumber: 4, location: 'City Center', tzShift: demoShift(4),
        fiScore: 5, fiLevel: 'Balanced',
        activities: [
          { id: 'd1', name: 'Trevi Fountain', type: 'attraction', time: '08:00', duration: 45, location: { name: 'Trevi Fountain', address: 'Piazza di Trevi', lat: 41.9009, lng: 12.4833 }, booked: false, fatigueImpact: 'chill', cost: 0, notes: 'Early morning for fewer crowds' },
          { id: 'd2', name: 'Spanish Steps', type: 'attraction', time: '09:15', duration: 45, location: { name: 'Spanish Steps', address: 'Piazza di Spagna', lat: 41.9058, lng: 12.4823 }, booked: false, fatigueImpact: 'chill', cost: 0 },
          { id: 'd3', name: 'Coffee at Antico Caffè Greco', type: 'restaurant', time: '10:30', duration: 45, location: { name: 'Antico Caffè Greco', address: 'Via dei Condotti, 86', lat: 41.9057, lng: 12.4806 }, booked: false, fatigueImpact: 'chill', cost: 15 },
          { id: 'd4', name: 'Shopping on Via del Corso', type: 'shopping', time: '11:45', duration: 180, location: { name: 'Via del Corso', lat: 41.9020, lng: 12.4790 }, booked: false, fatigueImpact: 'balanced', cost: 200 },
          { id: 'd5', name: 'Pantheon', type: 'attraction', time: '15:30', duration: 60, location: { name: 'Pantheon', address: 'Piazza della Rotonda', lat: 41.8986, lng: 12.4769 }, booked: false, fatigueImpact: 'chill', cost: 5 },
          { id: 'd6', name: 'Drinks at Salotto 42', type: 'restaurant', time: '18:00', duration: 90, location: { name: 'Salotto 42', address: 'Piazza di Pietra, 42', lat: 41.8995, lng: 12.4800 }, booked: false, fatigueImpact: 'chill', cost: 25 },
        ],
      },
      {
        // The demo's heavy day that is still ahead (JAR-2893). The trip is on its
        // third day: day 2 was yesterday and day 3 is today, and a re-plan moves
        // nothing that has begun. So this is the day Rebalance and Make this day
        // lighter have a plan for. The evening food tour is the stop that can
        // move: not booked, and in Rome, so it makes sense on another day.
        // Changing any stop on this trip means recording core's answers again
        // (data/demoRePlans.ts).
        id: 'day-5', date: demoDay(5), dayNumber: 5, location: 'Tivoli', tzShift: demoShift(5),
        fiScore: 7, fiLevel: 'Packed',
        activities: [
          { id: 'e1', name: 'Day Trip to Tivoli', type: 'transport', time: '09:00', duration: 60, location: { name: 'Roma Tiburtina Station', lat: 41.9106, lng: 12.5310 }, booked: true, fatigueImpact: 'balanced', cost: 8 },
          { id: 'e2', name: 'Villa d\'Este', type: 'attraction', time: '10:30', duration: 180, location: { name: 'Villa d\'Este', address: 'Piazza Trento, 5, Tivoli', lat: 41.9634, lng: 12.7956 }, booked: true, fatigueImpact: 'balanced', cost: 13 },
          { id: 'e3', name: 'Lunch at Ristorante Sibilla', type: 'restaurant', time: '14:00', duration: 90, location: { name: 'Ristorante Sibilla', lat: 41.9628, lng: 12.7965 }, booked: false, fatigueImpact: 'chill', cost: 45 },
          { id: 'e4', name: 'Villa Adriana', type: 'attraction', time: '16:00', duration: 150, location: { name: 'Villa Adriana', lat: 41.9430, lng: 12.7740 }, booked: true, fatigueImpact: 'balanced', cost: 10 },
          { id: 'e5', name: 'Train back to Rome', type: 'transport', time: '19:00', duration: 60, location: { name: 'Tivoli Station' }, booked: true, fatigueImpact: 'balanced', cost: 8 },
          { id: 'e6', name: 'Evening food tour in Trastevere', type: 'tour', time: '20:30', duration: 90, location: { name: 'Trastevere' }, booked: false, fatigueImpact: 'balanced', cost: 75 },
        ],
      },
      {
        id: 'day-6', date: demoDay(6), dayNumber: 6, location: 'Rome', tzShift: demoShift(6),
        fiScore: 2, fiLevel: 'Chill',
        activities: [
          { id: 'f1', name: 'Sleep In', type: 'rest', time: '09:00', duration: 120, location: { name: 'Hotel de Russie' }, booked: false, fatigueImpact: 'chill', cost: 0 },
          { id: 'f2', name: 'Spa Treatment', type: 'wellness', time: '11:00', duration: 120, location: { name: 'Hotel Spa' }, booked: true, fatigueImpact: 'chill', cost: 120 },
          { id: 'f3', name: 'Brunch at Roscioli Caffè', type: 'restaurant', time: '13:30', duration: 90, location: { name: 'Roscioli Caffè' }, booked: false, fatigueImpact: 'chill', cost: 35 },
          { id: 'f4', name: 'Borghese Gallery', type: 'attraction', time: '16:00', duration: 120, location: { name: 'Galleria Borghese', address: 'Piazzale Scipione Borghese, 5' }, booked: true, fatigueImpact: 'chill', cost: 15, notes: 'Timed entry required' },
          { id: 'f5', name: 'Dinner at Pierluigi', type: 'restaurant', time: '20:00', duration: 150, location: { name: 'Pierluigi', address: 'Piazza de Ricci, 144' }, booked: true, fatigueImpact: 'chill', cost: 120 },
        ],
      },
      {
        id: 'day-7', date: demoDay(7), dayNumber: 7, location: 'Rome', tzShift: demoShift(7),
        fiScore: 4, fiLevel: 'Balanced',
        activities: [
          { id: 'g1', name: 'Morning Walk', type: 'attraction', time: '08:00', duration: 90, location: { name: 'Villa Borghese Gardens' }, booked: false, fatigueImpact: 'chill', cost: 0 },
          { id: 'g2', name: 'Last Minute Shopping', type: 'shopping', time: '10:00', duration: 120, location: { name: 'Campo de\' Fiori Market' }, booked: false, fatigueImpact: 'chill', cost: 50 },
          { id: 'g3', name: 'Farewell lunch at Armando al Pantheon', type: 'restaurant', time: '13:00', duration: 120, location: { name: 'Armando al Pantheon' }, booked: true, fatigueImpact: 'chill', cost: 75 },
          { id: 'g4', name: 'Check out of Hotel de Russie', type: 'accommodation', time: '16:00', duration: 30, location: { name: 'Hotel de Russie' }, booked: true, fatigueImpact: 'chill', cost: 0 },
          { id: 'g5', name: 'Transfer to Airport', type: 'transport', time: '17:00', duration: 60, location: { name: 'Fiumicino Airport' }, booked: true, fatigueImpact: 'balanced', cost: 48 },
          { id: 'g6', name: 'Departure Flight', type: 'flight', time: '20:30', duration: 0, location: { name: 'FCO Terminal 3' }, booked: true, fatigueImpact: 'balanced', cost: 0 },
        ],
      },
    ],
    collaborators: [
      { id: 'c1', name: 'Sarah Thompson', email: 'sarah@example.com', permission: 'edit', status: 'accepted', avatar: '' },
    ],
    weather: { temperature: 68, feelsLike: 66, condition: 'sunny', humidity: 55, windSpeed: 8, icon: '☀️', forecast: [
      { day: 'Mon', high: 68, low: 52, icon: '☀️' },
      { day: 'Tue', high: 70, low: 54, icon: '⛅' },
      { day: 'Wed', high: 65, low: 50, icon: '🌧️' },
    ]},
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'trip-hawaii',
    name: 'Hawaiian Paradise',
    destination: 'Honolulu, Hawaii',
    country: 'USA',
    dates: { checkin: ymdFromToday(-30), checkout: ymdFromToday(-23) },
    status: 'Completed',
    travelers: { adults: 1, children: 0 },
    travelerNames: ['Alex Thompson'],
    budget: {
      total: 3500, spent: 1245, remaining: 2255, currency: 'USD', perPerson: 3500,
      categories: [
        { id: 'flights', name: 'Flights', allocated: 1200, spent: 890, icon: '✈️', color: '#0EA5E9' },
        { id: 'hotels', name: 'Hotels', allocated: 1400, spent: 280, icon: '🏨', color: '#6E4A62' },
        { id: 'food', name: 'Food', allocated: 500, spent: 75, icon: '🍽️', color: '#059669' },
        { id: 'activities', name: 'Activities', allocated: 400, spent: 0, icon: '🎭', color: '#C98A2E' },
      ],
      transactions: [],
    },
    fatigueIndex: {
      score: 3, level: 'Chill',
      breakdown: {
        travelTime: { score: 30, weight: 0.25, description: 'Time spent in transit' },
        activityDensity: { score: 40, weight: 0.30, description: 'Activities per day' },
        restPeriods: { score: 25, weight: 0.20, description: 'Beach relaxation' },
        jetLag: { score: 50, weight: 0.15, description: 'Timezone adjustment' },
        walkingDistance: { score: 30, weight: 0.10, description: 'Daily walking' },
      },
      recommendations: ['Perfect balance of activities and relaxation!'],
      lastUpdated: new Date().toISOString(),
    },
    stats: { restaurants: 6, attractions: 4, flights: 2, photos: 89, trains: 0, milesTraveled: 2467 },
    itinerary: [],
    collaborators: [],
    weather: { temperature: 82, feelsLike: 85, condition: 'sunny', humidity: 70, windSpeed: 12, icon: '🌴' },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'trip-dublin',
    name: 'Irish Adventure',
    destination: 'Dublin, Ireland',
    country: 'Ireland',
    dates: { checkin: ymdFromToday(28), checkout: ymdFromToday(35) },
    status: 'Draft',
    travelers: { adults: 3, children: 0 },
    travelerNames: ['Alex Thompson', 'Sarah Thompson', 'Mike Thompson'],
    budget: {
      total: 3000, spent: 0, remaining: 3000, currency: 'USD', perPerson: 1000,
      categories: [
        { id: 'flights', name: 'Flights', allocated: 1200, spent: 0, icon: '✈️', color: '#0EA5E9' },
        { id: 'hotels', name: 'Hotels', allocated: 1000, spent: 0, icon: '🏨', color: '#6E4A62' },
        { id: 'food', name: 'Food', allocated: 500, spent: 0, icon: '🍽️', color: '#059669' },
        { id: 'activities', name: 'Activities', allocated: 300, spent: 0, icon: '🎭', color: '#C98A2E' },
      ],
      transactions: [],
    },
    fatigueIndex: {
      score: 1, level: 'Chill',
      breakdown: {},
      recommendations: ['Start planning your activities!'],
      lastUpdated: new Date().toISOString(),
    },
    stats: { restaurants: 0, attractions: 0, flights: 0, photos: 0, trains: 0, milesTraveled: 0 },
    itinerary: [],
    collaborators: [],
    weather: { temperature: 58, feelsLike: 55, condition: 'cloudy', humidity: 75, windSpeed: 15, icon: '☁️' },
    plannedActivities: ['Trinity College Library', 'Guinness Storehouse', 'Temple Bar District', 'Cliffs of Moher Day Trip', 'Dublin Castle'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'trip-tokyo',
    name: 'Tokyo Discovery',
    destination: 'Tokyo, Japan',
    country: 'Japan',
    dates: { checkin: ymdFromToday(75), checkout: ymdFromToday(85) },
    status: 'Draft',
    travelers: { adults: 2, children: 0 },
    travelerNames: ['Alex Thompson'],
    budget: {
      total: 4500, spent: 0, remaining: 4500, currency: 'USD', perPerson: 2250,
      categories: [
        { id: 'flights', name: 'Flights', allocated: 1800, spent: 0, icon: '✈️', color: '#0EA5E9' },
        { id: 'hotels', name: 'Hotels', allocated: 1500, spent: 0, icon: '🏨', color: '#6E4A62' },
        { id: 'food', name: 'Food', allocated: 800, spent: 0, icon: '🍽️', color: '#059669' },
        { id: 'activities', name: 'Activities', allocated: 400, spent: 0, icon: '🎭', color: '#C98A2E' },
      ],
      transactions: [],
    },
    fatigueIndex: {
      score: 1, level: 'Chill',
      breakdown: {},
      recommendations: [],
      lastUpdated: new Date().toISOString(),
    },
    stats: { restaurants: 0, attractions: 0, flights: 0, photos: 0, trains: 0, milesTraveled: 0 },
    itinerary: [],
    collaborators: [],
    weather: { temperature: 65, feelsLike: 63, condition: 'sunny', humidity: 60, windSpeed: 10, icon: '🌸' },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

/** No baked covers. The demo's trips carry a destination, and whatever
 *  displays them resolves the photo from it, exactly as a live trip does. */
export const SAMPLE_TRIPS: JournalTrip[] = TRIPS;

// ============================================================================
// EXPLORE ACTIVITIES DATA
// ============================================================================

// Rome experiences, quoted in euros — which is what their `priceRange` strings
// have always said. Stated on each one now that the tab prints the currency it
// is given rather than a euro sign over everything (JAR-2003).
const ACTIVITIES: ExploreActivity[] = [
  {
    id: 'act-1',
    name: 'Vatican Museums & Sistine Chapel',
    description: 'Skip-the-line access to one of the world\'s greatest art collections',
    category: 'cultural',
    type: 'Tour',
    duration: '3 hours',
    durationMin: 180,
    avgPrice: 65,
    currency: 'EUR',
    priceRange: '€45-120',
    rating: 4.8,
    reviewCount: 12543,
    provider: 'GetYourGuide',
    location: { name: 'Vatican City', lat: 41.9065, lng: 12.4536 },
    highlights: ['Skip-the-line entry', 'Expert guide', 'Small group'],
    included: ['Entry tickets', 'Headsets', 'Professional guide'],
    providerUrl: 'https://www.getyourguide.com/',
  },
  {
    id: 'act-2',
    name: 'Pasta Making Class in Trastevere',
    description: 'Learn to make authentic Roman pasta with a local chef',
    category: 'food',
    type: 'Cooking Class',
    duration: '3 hours',
    durationMin: 180,
    avgPrice: 95,
    currency: 'EUR',
    priceRange: '€75-150',
    rating: 4.9,
    reviewCount: 3421,
    provider: 'Viator',
    location: { name: 'Trastevere, Rome', lat: 41.8855, lng: 12.4700 },
    highlights: ['Hands-on cooking', 'Wine pairing', 'Recipe booklet'],
    included: ['All ingredients', 'Wine', 'Lunch'],
    providerUrl: 'https://www.viator.com/',
  },
  {
    id: 'act-9',
    name: 'Trastevere Food Walk with a Local Host',
    description: 'A private evening of Roman street food and hidden trattorias, led by someone who lives there',
    category: 'food',
    type: 'Food Tour',
    duration: '3.5 hours',
    durationMin: 210,
    avgPrice: 89,
    currency: 'EUR',
    priceRange: '€70-140',
    rating: 4.9,
    reviewCount: 2140,
    provider: 'Withlocals',
    location: { name: 'Trastevere, Rome', lat: 41.8890, lng: 12.4690 },
    highlights: ['Private local host', 'Off-the-menu spots', 'Small group'],
    included: ['All tastings', 'Drinks', 'Local guide'],
    providerUrl: 'https://www.withlocals.com/',
  },
  {
    id: 'act-3',
    name: 'Colosseum Underground Tour',
    description: 'Explore the underground chambers and arena floor',
    category: 'cultural',
    type: 'Tour',
    duration: '2.5 hours',
    durationMin: 150,
    avgPrice: 75,
    currency: 'EUR',
    priceRange: '€55-95',
    rating: 4.7,
    reviewCount: 8932,
    provider: 'Official',
    location: { name: 'Colosseum, Rome', lat: 41.8902, lng: 12.4922 },
    highlights: ['Underground access', 'Arena floor', 'Roman Forum included'],
    included: ['Priority entry', 'Expert guide', 'Forum access'],
    providerUrl: 'https://www.coopculture.it/',
  },
  {
    id: 'act-4',
    name: 'Rome Wine Tasting Experience',
    description: 'Sample Italian wines with sommelier-led tasting',
    category: 'food',
    type: 'Wine Tasting',
    duration: '2 hours',
    durationMin: 120,
    avgPrice: 55,
    currency: 'EUR',
    priceRange: '€40-80',
    rating: 4.6,
    reviewCount: 2156,
    provider: 'Viator',
    location: { name: 'Centro Storico, Rome', lat: 41.8986, lng: 12.4769 },
    highlights: ['5 wine tastings', 'Cheese pairing', 'Expert sommelier'],
    included: ['Wines', 'Cheese & charcuterie', 'Tasting notes'],
    providerUrl: 'https://www.viator.com/',
  },
  {
    id: 'act-5',
    name: 'Vespa Tour of Rome',
    description: 'Explore Rome like a local on an iconic Vespa',
    category: 'adventure',
    type: 'Tour',
    duration: '4 hours',
    durationMin: 240,
    avgPrice: 145,
    currency: 'EUR',
    priceRange: '€120-180',
    rating: 4.9,
    reviewCount: 1893,
    provider: 'GetYourGuide',
    location: { name: 'Rome', lat: 41.9028, lng: 12.4964 },
    highlights: ['Vespa rental', 'Hidden gems', 'Photo stops'],
    included: ['Vespa & helmet', 'Guide', 'Gelato stop'],
    providerUrl: 'https://www.getyourguide.com/',
  },
  {
    id: 'act-6',
    name: 'Sunset Boat Cruise',
    description: 'Romantic sunset cruise along the Tiber River',
    category: 'tours',
    type: 'Cruise',
    duration: '1.5 hours',
    durationMin: 90,
    avgPrice: 45,
    currency: 'EUR',
    priceRange: '€35-65',
    rating: 4.5,
    reviewCount: 1245,
    provider: 'Local Operator',
    location: { name: 'Tiber River, Rome', lat: 41.9009, lng: 12.4700 },
    highlights: ['Sunset views', 'Prosecco included', 'Live commentary'],
    included: ['Boat ride', 'Prosecco', 'Guide'],
    providerUrl: 'https://www.rome-boat-tours.com/',
  },
  {
    id: 'act-7',
    name: 'Thermal Spa Experience',
    description: 'Relax at ancient Roman thermal baths',
    category: 'wellness',
    type: 'Spa',
    duration: '3 hours',
    durationMin: 180,
    avgPrice: 85,
    currency: 'EUR',
    priceRange: '€70-120',
    rating: 4.8,
    reviewCount: 876,
    provider: 'QC Terme',
    location: { name: 'Rome', lat: 41.9120, lng: 12.4850 },
    highlights: ['Multiple pools', 'Sauna access', 'Relaxation areas'],
    included: ['Full spa access', 'Towel & robe', 'Locker'],
    providerUrl: 'https://www.qcterme.com/',
  },
  {
    id: 'act-8',
    name: 'Street Food Walking Tour',
    description: 'Taste authentic Roman street food with a local guide',
    category: 'food',
    type: 'Food Tour',
    duration: '3 hours',
    durationMin: 180,
    avgPrice: 65,
    currency: 'EUR',
    priceRange: '€50-85',
    rating: 4.7,
    reviewCount: 2341,
    provider: 'Eating Europe',
    location: { name: 'Testaccio, Rome', lat: 41.8765, lng: 12.4753 },
    highlights: ['8 food stops', 'Local market visit', 'Off-the-beaten-path'],
    included: ['All tastings', 'Drinks', 'Guide'],
    providerUrl: 'https://www.eatingeurope.com/',
  },
];

/** No baked images. Each card resolves its own photo from its name and where
 *  it is, the same way a crawled card does. */
export const SAMPLE_ACTIVITIES: ExploreActivity[] = ACTIVITIES;

// ============================================================================
// PROPERTY DATA
// ============================================================================

export const SAMPLE_PROPERTY: Property = {
  id: 'prop-1',
  name: 'Hotel de Russie',
  type: 'Luxury Hotel',
  address: 'Via del Babuino, 9, 00187 Roma RM, Italy',
  phone: '+39 06 328881',
  website: 'https://www.roccofortehotels.com/hotels-and-resorts/hotel-de-russie/',
  rating: 4.9,
  reviewCount: 2156,
  amenities: [
    { id: 'wifi', name: 'Free WiFi', icon: '📶', category: 'essentials' },
    { id: 'pool', name: 'Swimming Pool', icon: '🏊', category: 'wellness' },
    { id: 'spa', name: 'Full-Service Spa', icon: '💆', category: 'wellness' },
    { id: 'gym', name: 'Fitness Center', icon: '💪', category: 'wellness' },
    { id: 'restaurant', name: 'On-site Restaurant', icon: '🍽️', category: 'dining' },
    { id: 'bar', name: 'Bar/Lounge', icon: '🍸', category: 'dining' },
    { id: 'room-service', name: '24-Hour Room Service', icon: '🛎️', category: 'services' },
    { id: 'concierge', name: 'Concierge', icon: '👨‍💼', category: 'services' },
    { id: 'parking', name: 'Valet Parking', icon: '🅿️', category: 'services' },
    { id: 'airport', name: 'Airport Shuttle', icon: '✈️', category: 'services' },
    { id: 'pet', name: 'Pet Friendly', icon: '🐕', category: 'policies' },
    { id: 'ac', name: 'Air Conditioning', icon: '❄️', category: 'essentials' },
  ],
  roomService: {
    available: true,
    hours: '24/7',
    menu: [
      { category: 'Breakfast', items: ['Continental Breakfast €25', 'Full English €35', 'Eggs Benedict €28'] },
      { category: 'All Day', items: ['Club Sandwich €22', 'Caesar Salad €18', 'Pasta of the Day €24'] },
      { category: 'Beverages', items: ['Espresso €5', 'Fresh Juice €8', 'Cocktails from €15'] },
    ],
  },
  dining: [
    { name: 'Le Jardin de Russie', type: 'Fine Dining', cuisine: 'Mediterranean', hours: '12:00 - 22:30', description: 'Award-winning garden restaurant' },
    { name: 'Stravinskij Bar', type: 'Bar', cuisine: 'Cocktails & Light Bites', hours: '10:00 - 01:00', description: 'Iconic bar with secret garden terrace' },
  ],
  services: [
    { name: 'Spa Treatments', description: 'Full menu of massages, facials, and body treatments', bookable: true },
    { name: 'Airport Transfer', description: 'Private car service to/from FCO or Ciampino', bookable: true },
    { name: 'Restaurant Reservations', description: 'Concierge assistance for Rome\'s best restaurants', bookable: true },
    { name: 'Tour Arrangements', description: 'Private guides and custom itineraries', bookable: true },
  ],
  checkIn: '15:00',
  checkOut: '12:00',
  location: { lat: 41.9100, lng: 12.4784, nearby: ['Piazza del Popolo - 2 min walk', 'Spanish Steps - 5 min walk', 'Villa Borghese - 3 min walk'] },
  reservation: { confirmation: 'HDR-7K2P9', checkInDate: demoDay(1), checkOutDate: demoDay(5), roomType: 'Deluxe Garden View', guests: 2, rate: '€520 / night', mobileKey: true },
  wifi: { network: 'DeRussie-Guest', password: 'Babuino9' },
  loyalty: { program: 'Rocco Forte Rewards', tier: 'Gold', points: 18450, memberId: 'RF-44821' },
};

const SAMPLE_PROPERTY_PALAZZO: Property = {
  id: 'prop-2',
  name: 'Palazzo Manfredi',
  type: 'Boutique Hotel',
  address: 'Via Labicana, 125, 00184 Roma RM, Italy',
  phone: '+39 06 77591380',
  website: 'https://www.palazzomanfredi.com/',
  rating: 4.8,
  reviewCount: 1342,
  amenities: [
    { id: 'wifi', name: 'Free WiFi', icon: '📶', category: 'essentials' },
    { id: 'ac', name: 'Air Conditioning', icon: '❄️', category: 'essentials' },
    { id: 'restaurant', name: 'Rooftop Restaurant', icon: '🍽️', category: 'dining' },
    { id: 'bar', name: 'Cocktail Bar', icon: '🍸', category: 'dining' },
    { id: 'gym', name: 'Fitness Center', icon: '💪', category: 'wellness' },
    { id: 'room-service', name: 'Room Service', icon: '🛎️', category: 'services' },
    { id: 'concierge', name: 'Concierge', icon: '👨‍💼', category: 'services' },
    { id: 'parking', name: 'Valet Parking', icon: '🅿️', category: 'services' },
  ],
  roomService: {
    available: true,
    hours: '07:00 - 23:00',
    menu: [
      { category: 'All Day', items: ['Cacio e Pepe €24', 'Roman Artichoke €18', 'Burrata €20'] },
      { category: 'Beverages', items: ['Espresso €4', 'Aperol Spritz €14', 'Wine by the glass from €12'] },
    ],
  },
  dining: [
    { name: 'Aroma', type: 'Fine Dining', cuisine: 'Michelin-Star Italian', hours: '12:30 - 23:00', description: 'Rooftop dining overlooking the Colosseum' },
  ],
  services: [
    { name: 'Private Colosseum Tour', description: 'After-hours guided access arranged by the concierge', bookable: true },
    { name: 'Airport Transfer', description: 'Private car to/from FCO or Ciampino', bookable: true },
  ],
  checkIn: '14:00',
  checkOut: '11:00',
  location: { lat: 41.8895, lng: 12.4969, nearby: ['Colosseum - 2 min walk', 'Roman Forum - 6 min walk', 'Domus Aurea - 4 min walk'] },
  reservation: { confirmation: 'PM-3X8Q1', checkInDate: demoDay(5), checkOutDate: demoDay(7), roomType: 'Colosseum View Suite', guests: 2, rate: '€680 / night', mobileKey: false },
  wifi: { network: 'Manfredi-Guest', password: 'Aroma2026' },
};

// All stays for the current trip (multiple stays render a clean stay selector).
export const SAMPLE_PROPERTIES: Property[] = [SAMPLE_PROPERTY, SAMPLE_PROPERTY_PALAZZO];

// Each sample trip gets its own stay, with dates that track that trip's dates,
// so the demo's Stays tab shows the trip you opened rather than Rome's on every
// trip (JAR-1995). Text details only, as the Rome stays above are.
const SAMPLE_PROPERTY_TOKYO: Property = {
  id: 'prop-tokyo',
  name: 'Park Hyatt Tokyo',
  type: 'Luxury Hotel',
  address: '3-7-1-2 Nishishinjuku, Shinjuku City, Tokyo 163-1055, Japan',
  phone: '+81 3-5322-1234',
  website: 'https://www.hyatt.com/park-hyatt/tyoph-park-hyatt-tokyo',
  rating: 4.8,
  reviewCount: 3012,
  amenities: [
    { id: 'wifi', name: 'Free WiFi', icon: '📶', category: 'essentials' },
    { id: 'ac', name: 'Air Conditioning', icon: '❄️', category: 'essentials' },
    { id: 'pool', name: 'Indoor Pool', icon: '🏊', category: 'wellness' },
    { id: 'spa', name: 'Club On The Park Spa', icon: '💆', category: 'wellness' },
    { id: 'gym', name: 'Fitness Center', icon: '💪', category: 'wellness' },
    { id: 'restaurant', name: 'New York Grill', icon: '🍽️', category: 'dining' },
    { id: 'bar', name: 'New York Bar', icon: '🍸', category: 'dining' },
    { id: 'concierge', name: 'Concierge', icon: '👨‍💼', category: 'services' },
  ],
  roomService: {
    available: true,
    hours: '24/7',
    menu: [
      { category: 'All Day', items: ['Wagyu Burger ¥4,800', 'Ramen ¥2,400', 'Sashimi Plate ¥3,800'] },
      { category: 'Beverages', items: ['Matcha ¥1,200', 'Japanese Whisky from ¥2,500'] },
    ],
  },
  dining: [
    { name: 'New York Grill', type: 'Fine Dining', cuisine: 'Steak & Seafood', hours: '11:30 - 22:00', description: 'On the 52nd floor with skyline views' },
    { name: 'Kozue', type: 'Fine Dining', cuisine: 'Japanese', hours: '11:30 - 21:30', description: 'Seasonal Japanese cuisine over the city' },
  ],
  services: [
    { name: 'Airport Transfer', description: 'Private car to/from Narita or Haneda', bookable: true },
    { name: 'Spa Treatments', description: 'Massages and facials at Club On The Park', bookable: true },
  ],
  checkIn: '15:00',
  checkOut: '12:00',
  location: { lat: 35.6855, lng: 139.6907, nearby: ['Shinjuku Station - 12 min walk', 'Tokyo Metropolitan Government - 8 min walk'] },
  reservation: { confirmation: 'PHT-9Q4K2', checkInDate: ymdFromToday(75), checkOutDate: ymdFromToday(85), roomType: 'Park Deluxe King', guests: 2, rate: '$620 / night', mobileKey: true },
  wifi: { network: 'ParkHyatt-Guest', password: 'Shinjuku52' },
};

const SAMPLE_PROPERTY_HAWAII: Property = {
  id: 'prop-hawaii',
  name: 'The Royal Hawaiian, a Luxury Collection Resort',
  type: 'Beach Resort',
  address: '2259 Kalakaua Ave, Honolulu, HI 96815, USA',
  phone: '+1 808-923-7311',
  website: 'https://www.marriott.com/hotels/travel/hnlrh-the-royal-hawaiian-a-luxury-collection-resort-waikiki/',
  rating: 4.6,
  reviewCount: 4188,
  amenities: [
    { id: 'wifi', name: 'Free WiFi', icon: '📶', category: 'essentials' },
    { id: 'beach', name: 'Private Beach', icon: '🏖️', category: 'wellness' },
    { id: 'pool', name: 'Oceanfront Pool', icon: '🏊', category: 'wellness' },
    { id: 'spa', name: 'Abhasa Spa', icon: '💆', category: 'wellness' },
    { id: 'restaurant', name: 'Beachfront Dining', icon: '🍽️', category: 'dining' },
    { id: 'bar', name: 'Mai Tai Bar', icon: '🍸', category: 'dining' },
  ],
  roomService: {
    available: true,
    hours: '06:00 - 23:00',
    menu: [{ category: 'All Day', items: ['Loco Moco $26', 'Poke Bowl $24', 'Island Fruit Plate $18'] }],
  },
  dining: [
    { name: 'Azure', type: 'Fine Dining', cuisine: 'Seafood', hours: '17:30 - 21:00', description: 'Oceanfront dining on Waikiki Beach' },
    { name: 'Mai Tai Bar', type: 'Bar', cuisine: 'Cocktails', hours: '10:00 - 23:00', description: 'Home of the Royal Hawaiian Mai Tai' },
  ],
  services: [
    { name: 'Surf Lessons', description: 'Private and group lessons on Waikiki Beach', bookable: true },
    { name: 'Airport Transfer', description: 'Shuttle to/from Honolulu (HNL)', bookable: true },
  ],
  checkIn: '15:00',
  checkOut: '11:00',
  location: { lat: 21.2766, lng: -157.8292, nearby: ['Waikiki Beach - on site', 'Diamond Head - 10 min drive'] },
  reservation: { confirmation: 'TRH-5M8T3', checkInDate: ymdFromToday(-30), checkOutDate: ymdFromToday(-23), roomType: 'Historic Ocean View', guests: 1, rate: '$520 / night', mobileKey: true },
  wifi: { network: 'RoyalHawaiian-Guest', password: 'Waikiki1927' },
};

const SAMPLE_PROPERTY_DUBLIN: Property = {
  id: 'prop-dublin',
  name: 'The Shelbourne, Autograph Collection',
  type: 'Historic Hotel',
  address: "27 St Stephen's Green, Dublin 2, D02 K224, Ireland",
  phone: '+353 1 663 4500',
  website: 'https://www.marriott.com/hotels/travel/dubak-the-shelbourne-autograph-collection/',
  rating: 4.6,
  reviewCount: 2765,
  amenities: [
    { id: 'wifi', name: 'Free WiFi', icon: '📶', category: 'essentials' },
    { id: 'ac', name: 'Air Conditioning', icon: '❄️', category: 'essentials' },
    { id: 'spa', name: 'The Spa', icon: '💆', category: 'wellness' },
    { id: 'gym', name: 'Fitness Center', icon: '💪', category: 'wellness' },
    { id: 'restaurant', name: 'The Saddle Room', icon: '🍽️', category: 'dining' },
    { id: 'bar', name: 'The Horseshoe Bar', icon: '🍸', category: 'dining' },
    { id: 'concierge', name: 'Concierge', icon: '👨‍💼', category: 'services' },
  ],
  roomService: {
    available: true,
    hours: '24/7',
    menu: [{ category: 'All Day', items: ['Irish Stew €26', 'Fish & Chips €24', 'Afternoon Tea €55'] }],
  },
  dining: [
    { name: 'The Saddle Room', type: 'Fine Dining', cuisine: 'Irish Seafood & Steak', hours: '12:30 - 22:00', description: 'A Dublin institution since 1824' },
    { name: 'The Horseshoe Bar', type: 'Bar', cuisine: 'Cocktails & Light Bites', hours: '12:00 - 23:30', description: 'Historic meeting place off the lobby' },
  ],
  services: [
    { name: 'Airport Transfer', description: 'Private car to/from Dublin (DUB)', bookable: true },
    { name: 'Afternoon Tea', description: "Served daily in the Lord Mayor's Lounge", bookable: true },
  ],
  checkIn: '15:00',
  checkOut: '12:00',
  location: { lat: 53.3382, lng: -6.2546, nearby: ["St Stephen's Green - across the street", 'Grafton Street - 3 min walk', 'Trinity College - 8 min walk'] },
  reservation: { confirmation: 'SHB-2J7W6', checkInDate: ymdFromToday(28), checkOutDate: ymdFromToday(35), roomType: 'Classic Double', guests: 3, rate: '$410 / night', mobileKey: false },
  wifi: { network: 'Shelbourne-Guest', password: 'Stephens1824' },
};

// Each demo trip's own stays, read by trip id so the Stays tab shows the trip
// you opened (JAR-1995). Rome keeps its two; the others have one each.
export const SAMPLE_STAYS_BY_TRIP: Record<string, Property[]> = {
  'trip-rome': SAMPLE_PROPERTIES,
  'trip-tokyo': [SAMPLE_PROPERTY_TOKYO],
  'trip-hawaii': [SAMPLE_PROPERTY_HAWAII],
  'trip-dublin': [SAMPLE_PROPERTY_DUBLIN],
};

// ============================================================================
// NOTIFICATIONS DATA
// ============================================================================

export const SAMPLE_NOTIFICATIONS: Notification[] = [
  { id: 'n1', type: 'trip-invite', title: 'Trip Invitation', message: 'Sarah invited you to join "Tokyo Discovery"', read: false, timestamp: new Date().toISOString(), actionable: true, tripId: 'trip-tokyo' },
  { id: 'n2', type: 'booking', title: 'Reservation Confirmed', message: 'Your Vatican Museums tour is confirmed', read: false, timestamp: new Date(Date.now() - 3600000).toISOString(), tripId: 'trip-rome' },
  { id: 'n3', type: 'reminder', title: 'Trip Reminder', message: 'Your Roman Holiday trip is under way. Today\'s plan is ready.', read: true, timestamp: new Date(Date.now() - 86400000).toISOString(), tripId: 'trip-rome' },
  { id: 'n4', type: 'price-alert', title: 'Price Drop Alert', message: 'Colosseum tour price dropped by 15%', read: true, timestamp: new Date(Date.now() - 172800000).toISOString(), tripId: 'trip-rome' },
];

// ============================================================================
// EXPORT DEFAULT ACTIVE TRIP
// ============================================================================

export const getActiveTrip = (): JournalTrip => {
  return SAMPLE_TRIPS.find(t => t.status === 'Draft') || SAMPLE_TRIPS[0];
};

export const getTripById = (id: string): JournalTrip | undefined => {
  return SAMPLE_TRIPS.find(t => t.id === id);
};
